/**
 * THE ELEVEN — what Outform's own front end needs from history, and how much of
 * it we can actually build today.
 *
 * ⚠️ THIS IS THE SECOND HALF OF A PAIR, AND IT DELIBERATELY DOES NOT REDRAW THE
 * FIRST. Atlas's `./data-programme` answers "what happens to a client's data,
 * end to end" — six stages, one human step, the loop. That diagram is correct
 * and it is mounted on the R&D board; this file imports nothing from it and
 * repeats none of it. `skill://diagrams` says choose by the QUESTION the reader
 * is asking, and this one asks a different question:
 *
 *     the pipeline PUBLISHES a corpus — so what comes OUT of it,
 *     and which of it is blocked?
 *
 * ── WHERE THE ELEVEN COME FROM ─────────────────────────────────────────────
 * On 28 Aug 2026 Outform sent an interactive prototype of their estimate output
 * document (v4.1). Its data model is 27 hardcoded arrays and the file makes no
 * network call of any kind. Sorted by where each array would REALLY have to
 * come from, exactly eleven can only come from normalized job history. Those
 * eleven are this diagram. Every one of them is a literal in their file today.
 *
 * ── THE ARGUMENT THE LAYOUT IS MAKING ──────────────────────────────────────
 *
 * 1 · THE SPLIT IS 5 / 4 / 2, NOT ELEVEN. Grouping them by BLOCKER rather than
 *     by screen is the whole point of the middle column. Five are derivable
 *     from the 51 files they have already given us — we could start on Monday.
 *     Four need quote outcomes. Two need the loss debriefs. Naming eleven as a
 *     single lump hides that most of the value is behind two datasets and not
 *     behind engineering.
 *
 * 2 · THE TWO RED SOURCES ARE THE SAME TWO THEY HAD TO FAKE. Their prototype's
 *     `BANDS` array is seven typed-in rows summing to exactly 351, and the
 *     competitor ranges are "modelled". They did not fake the cost history,
 *     because the cost history exists. An involuntary confirmation, and the
 *     reason the red boxes sit at the far left rather than in a footnote.
 *
 * 3 · DASHED MEANS "RENDERS TODAY, FROM NOTHING". Six of the eleven already
 *     appear on their page. That is what makes this urgent rather than
 *     academic: the screens are built and populated with invented numbers, so
 *     the gap is invisible to anyone reading the prototype.
 *
 * 4 · THE THIRD COLUMN IS THEIR OWN TAB NAMES. Not our vocabulary — theirs, so
 *     the mapping can be checked against the file in front of them.
 *
 * Nothing here has a hand-written width; the engine measures text, containers
 * measure children, wires resolve against placed rects.
 */
import { Canvas, leaf, box, type Diagram } from "./c4";

const ELEVEN: Diagram = (() => {
  // ── WHAT THE PIPELINE PUBLISHES ────────────────────────────────────────────
  // Two of the four are red for the same reason Atlas's sources column has red
  // in it: we have never been given them. Kept to four leaves because this
  // column is the PREMISE of the diagram, not its subject.
  const corpus = box("e-cor", "THE CORPUS PUBLISHES", "col", [
    leaf("e-cost", "Costed job history", "[1,487 jobs · 51 held]", "FileSpreadsheet"),
    leaf("e-vq", "Vendor quotes", "[by part · dated]", "Receipt"),
    leaf("e-out", "Quote outcomes", "[win/loss + price · NO ACCESS]", "Trophy", "gate"),
    leaf("e-deb", "Loss debriefs", "[competitor price · NO ACCESS]", "MessagesSquare", "gate"),
  ], { gap: 12 });

  // ── ① FIVE WE CAN BUILD NOW ────────────────────────────────────────────────
  // Every one of these rests on cost and vendor data that is already in the 51
  // files. This is the column that makes Monday a build conversation rather
  // than a wait.
  const now = box("e-now", "① BUILDABLE FROM WHAT WE HOLD — five", "col", [
    leaf("e-bench", "Category benchmark", "[7 of their 14]", "BarChart3"),
    leaf("e-comps", "Comparable set", "[similarity ranked]", "Layers"),
    leaf("e-paths", "Manufacturing paths", "[6 geographies]", "Globe"),
    leaf("e-drv", "China-vs-US drivers", "[11 categories]", "Scale"),
    leaf("e-vol", "Volume ladder", "[5 rungs · arithmetic]", "TrendingDown"),
  ], { gap: 12 });

  // ── ② FOUR BLOCKED ON OUTCOMES ─────────────────────────────────────────────
  // The 40% weight in their own §3.7.1 lives entirely in this box. No amount of
  // engineering substitutes for the column that records whether a job was won.
  const blockedOut = box("e-bo", "② BLOCKED — no quote outcomes — four", "col", [
    leaf("e-curve", "Win-rate curve", "[12 points]", "Activity", "gate"),
    leaf("e-bands", "CM band table", "[the invented 351]", "Table2", "gate"),
    leaf("e-mods", "Deal modifiers", "[± points on the curve]", "SlidersHorizontal", "gate"),
    leaf("e-att", "Option attach rates", "[9 options]", "Percent", "gate"),
  ], { gap: 12 });

  // ── ③ TWO BLOCKED ON DEBRIEFS ──────────────────────────────────────────────
  const blockedDeb = box("e-bd", "③ BLOCKED — no debriefs — two", "col", [
    leaf("e-comp", "Competitor ranges", "[5 rivals priced]", "Crosshair", "gate"),
    leaf("e-riv", "Rival playbook", "[beat them on / lose on]", "Swords", "gate"),
  ], { gap: 12 });

  // ── WHERE EACH LANDS ───────────────────────────────────────────────────────
  // Their tab names, verbatim, so the mapping is checkable against the file
  // rather than against our description of it.
  const screen = box("e-scr", "THEIR OWN TABS", "col", [
    leaf("e-t1", "Overview", "[three prices]", "LayoutDashboard"),
    leaf("e-t2", "Cost Breakdown", "[benchmark column]", "Table"),
    leaf("e-t3", "Pricing & Conversion", "[the curve]", "LineChart"),
    leaf("e-t4", "Market Benchmark", "[competitor ranges]", "Target"),
    leaf("e-t5", "Scope & Upsell", "[attach rates]", "ListPlus"),
  ], { gap: 12 });

  return {
    key: "e11",
    title: "The eleven — what their front end needs from history",
    subtitle:
      "Their v4.1 prototype hardcodes all eleven · five are buildable from the 51 files we already hold · six wait on two datasets nobody has given us · 2026-08-28",
    padBottom: 122,
    cols: [
      { root: corpus, align: "center" },
      { root: now, align: "center", dy: -150 },
      { root: blockedOut, align: "center", dy: 40 },
      { root: blockedDeb, align: "center", dy: 40 },
      { root: screen, align: "center" },
    ],
    wires: [
      // THE BUILDABLE LINE. Solid, numbered, and it is the only path on this
      // diagram that runs end to end without crossing a gate.
      { a: "e-cost", b: "e-now", as: "r", bs: "l", num: 1, label: "derive" },
      { a: "e-vq", b: "e-now", as: "r", bs: "l", num: 2 },
      { a: "e-now", b: "e-scr", as: "r", bs: "l", num: 3, label: "renders" },

      // THE TWO BLOCKED LINES. Red at the source, because the break is the
      // ACCESS and not the engineering.
      { a: "e-out", b: "e-bo", as: "b", bs: "l", red: true, num: 4 },
      { a: "e-deb", b: "e-bd", as: "b", bs: "l", red: true, num: 5 },

      // WHAT RENDERS TODAY ANYWAY. Dashed, and this is the uncomfortable half:
      // six of the eleven are already on their page, populated with numbers
      // that came from nowhere. Routed to the same tabs so the overlap is
      // visible rather than argued.
      { a: "e-bo", b: "e-scr", as: "r", bs: "b", dashed: true, label: "invented" },
      { a: "e-bd", b: "e-scr", as: "r", bs: "b", dashed: true },
    ],
    legend: [
      { style: "solid", label: "buildable from the 51 files we already hold — five of the eleven, and enough to start on Monday" },
      { style: "dash", label: "renders on their prototype TODAY from hardcoded numbers — which is why the gap is invisible to anyone reading the screen" },
      { style: "red", label: "a dataset we have never been given: quote outcomes carry the 40% weight in their own scoring model, and the debriefs are the only source of a competitor's price" },
    ],
  };
})();

/** The eleven derivations their own front end needs, split by what blocks each. */
export function OutformElevenDiagram() {
  return <Canvas d={ELEVEN} mode="contrast" />;
}
